import { Outlet, Link, useLocation } from "react-router-dom";
import EtatConnexion from "@/components/EtatConnexion";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/hooks/useAuth";
import { useEcranEtroit } from "@/lib/hooks/useEcranEtroit";
import { logout } from "@/lib/firebase/auth";
import { getDocuments } from "@/lib/firebase/firestore";
import { COLLECTIONS } from "@/lib/constants";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import NotificationPrompt from "@/components/NotificationPrompt";
import NewOrderAlarm from "@/components/NewOrderAlarm";
import AlarmePreparationMagasin from "@/components/AlarmePreparationMagasin";
import { LayoutDashboard, Package, LogOut, Wallet } from "lucide-react";
import BoutonLangue from "@/components/BoutonLangue";
import { useLangue } from "@/lib/i18n";

export default function CollaborateurLayout() {
  // Une seule copie de la page montee (01/11/2026) : voir useEcranEtroit.
  const etroit = useEcranEtroit();
  // Le sens de lecture est pose sur les conteneurs de CETTE mise en page :
  // l'administration doit rester en francais et lisible, quel que soit le
  // choix fait ici depuis le meme navigateur.
  const { t, rtl } = useLangue();
  const { user } = useAuth();
  const location = useLocation();

  /**
   * Le magasin du collaborateur.
   *
   * Meme recherche que son tableau de bord : d'abord ce que l'ouverture de
   * session a deja charge, sinon sa fiche collaborateur. Sans ce magasin,
   * l'alarme n'a pas le droit de lire les commandes.
   */
  const [magasin, setMagasin] = useState<string | null>(null);

  useEffect(() => {
    if (!user || user.role !== "COLLABORATEUR") return;
    if (user.storeId) {
      setMagasin(user.storeId);
      return;
    }
    let annule = false;
    (async () => {
      try {
        const fiches = await getDocuments(
          COLLECTIONS.COLLABORATEURS,
          [{ field: "userId", operator: "==", value: user.uid }],
          undefined,
          "asc",
          1,
        );
        let trouve: string | null = fiches[0]?.storeId ?? null;
        if (!trouve) {
          const fiche = await getDoc(
            doc(db, COLLECTIONS.COLLABORATEURS, user.uid),
          );
          trouve = fiche.exists() ? (fiche.data()?.storeId ?? null) : null;
        }
        if (!annule) setMagasin(trouve);
      } catch {
        /* Sans magasin, l'alarme reste muette plutot que refusee. */
      }
    })();
    return () => {
      annule = true;
    };
  }, [user?.uid, user?.role, user?.storeId]);

  const handleLogout = async () => {
    await logout();
  };

  const navItems = [
    { path: "/collaborateur/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/collaborateur/orders", label: "Commandes", icon: Package },
    // Ce que Glovo nous retient, avec les photos : celui qui prepare doit
    // voir ce qu'on lui reproche, sinon rien ne change la fois d'apres.
    { path: "/collaborateur/caisse", label: "Ma caisse", icon: Wallet },
    { path: "/collaborateur/remboursements", label: "Remboursements", icon: Wallet },
  ];

  return (
    <div className="min-h-screen bg-gray-50" dir={rtl ? "rtl" : "ltr"}>
      {/* Notification Permission Prompt */}
      <NotificationPrompt />
      
      {/* Mobile: Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 shadow-lg">
        <div className="flex justify-around">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex-1 py-3 px-2 text-center text-xs min-h-[44px] flex flex-col items-center justify-center transition-colors ${
                  location.pathname === item.path
                    ? "text-primary border-t-2 border-primary bg-primary/5"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <Icon className="w-5 h-5 mb-1" />
                <span className="text-[10px] font-medium">{t(item.label)}</span>
              </Link>
            );
          })}
          {/* La bascule de langue est DANS la barre du bas : c'est la seule
              chose toujours visible sur un telephone. Ailleurs, quelqu'un qui
              ne lit pas le francais ne la trouverait jamais. */}
          <div className="flex-shrink-0 py-2 px-2 flex items-center justify-center">
            <BoutonLangue />
          </div>
          <button
            onClick={handleLogout}
            className="flex-1 py-3 px-2 text-center text-xs min-h-[44px] flex flex-col items-center justify-center text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5 mb-1" />
            <span className="text-[10px] font-medium">{t("Déco")}</span>
          </button>
        </div>
      </nav>

      {/* Desktop: Sidebar */}
      <div className="hidden md:flex">
        <aside className="w-64 bg-white border-r border-gray-200 min-h-screen">
          <div className="p-4">
            <div className="flex items-center justify-between gap-2">
              <h1 className="text-xl font-bold">Natura Beldi</h1>
              <BoutonLangue />
            </div>
            <p className="text-sm text-gray-600">{t("Collaborateur")}</p>
          </div>
          <nav className="mt-8">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                    location.pathname === item.path
                      ? "bg-primary text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{t(item.label)}</span>
                </Link>
              );
            })}
          </nav>
          <div className="absolute bottom-0 w-64 p-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">{user?.name}</p>
            {!etroit && <EtatConnexion />}
            <button
              onClick={handleLogout}
              className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              {t("Déconnexion")}
            </button>
          </div>
        </aside>
        <main className="flex-1 p-6 pb-20 md:pb-6">
          {!etroit && <Outlet />}
        </main>
      </div>

      {/* Mobile: Main Content */}
      <div className="md:hidden pb-20" dir={rtl ? "rtl" : "ltr"}>
        {/* En affichage etroit, la barre laterale n'existe pas : sans cette
            bande, ni le nom ni l'etat de connexion ne sont visibles. */}
        {etroit && <EtatConnexion nom={user?.name} barre />}
        <main className="p-4">
          {etroit && <Outlet />}
        </main>
      </div>

      {/* Alerte sonore a l'arrivee d'une commande.
          Limitee a son magasin : les regles Firestore ne lui laissent pas
          lire les autres, et une requete trop large serait refusee en bloc. */}
      <NewOrderAlarm
        lienCommandes="/collaborateur/dashboard"
        storeId={magasin}
        attendreStore
        parApi
        sansBoutonTest
      />

      {/* Les rayons que le magasin prepare lui-meme — legumes prepares,
          legumes decoupes, recettes decoupees, salade d'herbes. Le
          fournisseur garde son alarme : ici on AJOUTE celle du responsable,
          parce que c'est lui qui fait la decoupe. */}
      <AlarmePreparationMagasin storeId={magasin} />
    </div>
  );
}

